/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import { MathMessageEntry } from '../../../types/model';
import React, { createContext, FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { mathClient } from '../../../api/MathClient';
import { CompiledProblem } from '../../../api/types';

export type SolverConfig = {
    allowAssistVisualization: boolean;
    immediateAssist: boolean;
    assistDelay: number;
    popoverStrategy: 'cursor' | 'hover';
};

type SolverContextType = {
    mathMessages: Map<string, MathMessageEntry>;
    problem: CompiledProblem;
    config: SolverConfig;
};

export const SolverContext = createContext<SolverContextType>({
    mathMessages: new Map(),
    problem: undefined,
    config: {
        allowAssistVisualization: false,
        immediateAssist: false,
        assistDelay: 0,
        popoverStrategy: 'cursor',
    },
});

export const SolverContextProvider: FC<
    PropsWithChildren<{
        problem: CompiledProblem;
        config: SolverConfig;
    }>
> = ({ problem, config, children }) => {
    const [mathMessages, setMathMessages] = useState<Map<string, MathMessageEntry>>(new Map());

    useEffect(() => {
        mathClient.getAssistMessages().then((response) => {
            const values: MathMessageEntry[] = Object.values(response);
            setMathMessages(Object.keys(response).reduce((p, k, i) => p.set(k, values[i]), new Map()));
        });
    }, []);

    const context: SolverContextType = useMemo(() => ({ mathMessages, problem, config }), [mathMessages, problem, config]);

    return <SolverContext.Provider value={context}>{children}</SolverContext.Provider>;
};
